import React from 'react';
import { Document, Page, View, StyleSheet } from '@react-pdf/renderer';
import { PDF_THEMES, DEFAULT_THEME } from '../themes';
import PdfHeader from '../components/PdfHeader';
import PdfFooter from '../components/PdfFooter';
import PdfSummaryCards from '../components/PdfSummaryCards';
import { PdfTableHead, PdfTableRow, PdfTableFoot } from '../components/PdfTable';
import { fmtINR, fmtDate, cap } from '../pdfUtils';

const s = StyleSheet.create({
  page:    { fontFamily: 'NotoSans', paddingBottom: 60 },
  content: { marginHorizontal: 40, marginTop: 12 },
});

const COLS = [
  { label: 'Offer No',  flex: 1 },
  { label: 'Date',      flex: 0.9 },
  { label: 'Member',    flex: 1.6 },
  { label: 'Category',  flex: 1.6 },
  { label: 'Offered',   flex: 1, align: 'right' },
  { label: 'Paid',      flex: 1, align: 'right' },
  { label: 'Pending',   flex: 1, align: 'right' },
  { label: 'Status',    flex: 0.9 },
];

export default function OfferingsReportDocument({ data, theme: themeKey, orientation, sections }) {
  const t = PDF_THEMES[themeKey] || PDF_THEMES[DEFAULT_THEME];
  const rows = Array.isArray(data.rows) ? data.rows : [];
  const summary = data.summary || {};

  const totOffered = summary.totalOffered ?? rows.filter((r) => r.status !== 'cancelled').reduce((sum, r) => sum + Number(r.offeredAmount || 0), 0);
  const totPaid    = summary.totalPaid    ?? rows.filter((r) => r.status !== 'cancelled').reduce((sum, r) => sum + Number(r.paidAmount    || 0), 0);
  const totPending = summary.totalPending ?? rows.filter((r) => r.status !== 'cancelled').reduce((sum, r) => sum + Number(r.pendingAmount || 0), 0);

  return (
    <Document>
      <Page size="A4" orientation={orientation || 'landscape'} style={[s.page, { backgroundColor: t.white }]}>
        {sections.header && <PdfHeader derasar={data.derasar} reportTitle="Offerings Report" theme={t} />}
        <View style={s.content}>
          {sections.summaryCards && (
            <PdfSummaryCards theme={t} items={[
              { label: 'Total Offered', value: fmtINR(totOffered), color: t.body },
              { label: 'Total Paid',    value: fmtINR(totPaid),    color: t.statusGreen },
              { label: 'Total Pending', value: fmtINR(totPending), color: t.statusRed },
              { label: 'Entries',       value: String(rows.length), color: t.primary },
            ]} />
          )}
          <PdfTableHead cols={COLS} theme={t} />
          {rows.map((row, i) => (
            <PdfTableRow key={row.id ?? i} cols={COLS} index={i} theme={t}
              values={[
                row.offerNo || '—',
                fmtDate(row.offeringDate),
                row.memberName || '—',
                row.category || '—',
                fmtINR(row.offeredAmount),
                fmtINR(row.paidAmount),
                fmtINR(row.pendingAmount),
                cap(row.status),
              ]}
            />
          ))}
          <PdfTableFoot cols={COLS}
            values={['', '', '', `Total (excl. cancelled)`, fmtINR(totOffered), fmtINR(totPaid), fmtINR(totPending), '']}
            theme={t}
          />
        </View>
        {sections.footer && <PdfFooter theme={t} />}
      </Page>
    </Document>
  );
}
